import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { supabase } from '@/integrations/supabase/client';
import { User, Target, Calendar, Flame, CheckCircle2, Circle, ChevronRight } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import type {
  WellnessUserChallenge,
  WellnessChallengeCheckin,
  WellnessChallengeTemplate
} from '@/types/wellnessChallenges';
import { ChallengeCorrelationsPanel } from './ChallengeCorrelationsPanel';
import { ChallengeDetailDrawer } from './ChallengeDetailDrawer';

interface ChallengeWithProgress extends WellnessUserChallenge {
  templateTitle?: string | null;
  templateShortLabel?: string | null;
  todayCheckin?: WellnessChallengeCheckin | null;
  completedDays: number;
  totalCheckins: number;
  streak: number;
  templateMetricSource?: string | null;
  templateMetricKey?: string | null;
  template?: WellnessChallengeTemplate | null;
}

export function MyPersonalChallenges() {
  const today = new Date().toISOString().slice(0, 10);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [selectedChallenge, setSelectedChallenge] = useState<ChallengeWithProgress | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  const handleOpenDetail = (challenge: ChallengeWithProgress) => {
    setSelectedChallenge(challenge);
    setIsDetailOpen(true);
  };

  const handleCloseDetail = () => {
    setIsDetailOpen(false);
    setSelectedChallenge(null);
  };

  const { data: user } = useQuery({
    queryKey: ['user'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      return user;
    },
  });

  const { data: challenges, isLoading, refetch } = useQuery({
    queryKey: ['personal-wellness-challenges', user?.id, today],
    queryFn: async () => {
      if (!user?.id) return [];

      // Fetch user's active wellness challenges with template info
      const { data: userChallenges, error } = await supabase
        .from('wellness_user_challenges')
        .select(`
          *,
          wellness_challenge_templates (
            title,
            short_label,
            description,
            metric_source,
            metric_key
          )
        `)
        .eq('user_id', user.id)
        .eq('status', 'active')
        .order('created_at', { ascending: true });

      if (error) {
        console.error('Error fetching wellness challenges', error);
        return [];
      }

      if (!userChallenges || userChallenges.length === 0) return [];

      const challengeIds = userChallenges.map((c: any) => c.id);

      // Fetch all checkins for these challenges
      const { data: allCheckins, error: checkinsError } = await supabase
        .from('wellness_challenge_checkins')
        .select('*')
        .in('user_challenge_id', challengeIds)
        .order('checkin_date', { ascending: false });

      if (checkinsError) {
        console.error('Error fetching challenge checkins', checkinsError);
      }

      // Calculate progress for each challenge
      return userChallenges.map((c: any) => {
        const challengeCheckins = (allCheckins || []).filter(
          (ci: any) => ci.user_challenge_id === c.id
        );
        
        const completedDays = challengeCheckins.filter((ci: any) => ci.completed).length;
        const totalCheckins = challengeCheckins.length;
        const todayCheckin = challengeCheckins.find((ci: any) => ci.checkin_date === today);
        
        // Calculate streak (consecutive completed days ending today or yesterday)
        let streak = 0;
        const sortedCompleted = challengeCheckins
          .filter((ci: any) => ci.completed)
          .map((ci: any) => ci.checkin_date)
          .sort((a: string, b: string) => b.localeCompare(a));
        
        if (sortedCompleted.length > 0) {
          const checkDate = new Date();
          for (const dateStr of sortedCompleted) {
            const checkinDate = new Date(dateStr);
            const diffDays = Math.floor((checkDate.getTime() - checkinDate.getTime()) / (1000 * 60 * 60 * 24));
            if (diffDays <= 1) {
              streak++;
              checkDate.setTime(checkinDate.getTime());
            } else {
              break;
            }
          }
        }

        return {
          ...c,
          templateTitle: c.wellness_challenge_templates?.title,
          templateShortLabel: c.wellness_challenge_templates?.short_label,
          templateMetricSource: c.wellness_challenge_templates?.metric_source,
          templateMetricKey: c.wellness_challenge_templates?.metric_key,
          todayCheckin: todayCheckin || null,
          completedDays,
          totalCheckins,
          streak,
          template: c.wellness_challenge_templates ? {
            id: c.template_id || '',
            slug: '',
            title: c.wellness_challenge_templates.title || '',
            short_label: c.wellness_challenge_templates.short_label || '',
            description: c.wellness_challenge_templates.description || null,
            category: c.category,
            metric_source: c.wellness_challenge_templates.metric_source || 'custom',
            metric_key: c.wellness_challenge_templates.metric_key,
            target_type: c.target_type,
            default_target_value: null,
            unit: c.unit,
            default_duration_days: c.duration_days,
            is_user_configurable: true,
            is_active: true,
            created_at: ''
          } : null
        } as ChallengeWithProgress;
      });
    },
    enabled: !!user?.id,
  });

  const handleCheckin = async (challengeId: string, completed: boolean) => {
    if (!user?.id) return;
    setSavingId(challengeId);

    const payload = {
      user_challenge_id: challengeId,
      checkin_date: today,
      completed
    };

    const { error } = await supabase
      .from('wellness_challenge_checkins')
      .upsert(payload, { onConflict: 'user_challenge_id,checkin_date' });

    if (error) {
      console.error('Error saving wellness challenge checkin', error);
    }

    setSavingId(null);
    refetch();
  };

  const getDayProgress = (challenge: ChallengeWithProgress) => {
    const start = new Date(challenge.start_date);
    const end = new Date(challenge.end_date);
    const now = new Date();
    
    const totalDays = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    const daysPassed = Math.ceil((now.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    const currentDay = Math.min(Math.max(daysPassed, 1), totalDays);
    
    return { currentDay, totalDays };
  };

  const getCompletionPercentage = (challenge: ChallengeWithProgress) => {
    const { totalDays } = getDayProgress(challenge);
    if (totalDays === 0) return 0;
    return Math.round((challenge.completedDays / totalDays) * 100);
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <User className="h-5 w-5 text-primary" />
            My Personal Challenges
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Loading your challenges...</p>
        </CardContent>
      </Card>
    );
  }

  if (!challenges || challenges.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <User className="h-5 w-5 text-primary" />
            My Personal Challenges
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-6 space-y-3">
            <Target className="h-12 w-12 mx-auto text-muted-foreground/50" />
            <p className="text-sm text-muted-foreground">
              You don't have any active personal wellness challenges yet.
            </p>
            <p className="text-xs text-muted-foreground">
              Start a challenge to track your progress and see how consistent habits impact your wellbeing.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <User className="h-5 w-5 text-primary" />
          My Personal Challenges
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Your private wellness focuses. Check in daily to build momentum.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {challenges.map((challenge) => {
          const title = challenge.custom_title || challenge.templateShortLabel || challenge.templateTitle || 'Personal focus';
          const { currentDay, totalDays } = getDayProgress(challenge);
          const completionPct = getCompletionPercentage(challenge);
          const hasCheckinToday = !!challenge.todayCheckin;
          const completedToday = challenge.todayCheckin?.completed === true;

          return (
            <div
              key={challenge.id}
              className="border rounded-lg p-4 bg-muted/30 space-y-3"
            >
              {/* Header - clickable to open detail */}
              <div 
                className="flex items-start justify-between gap-2 cursor-pointer group"
                onClick={() => handleOpenDetail(challenge)}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="font-medium text-foreground group-hover:text-primary transition-colors">{title}</h4>
                    <Badge variant="outline" className="text-xs capitalize">
                      {challenge.category}
                    </Badge>
                  </div>
                  {challenge.custom_goal && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {challenge.custom_goal}
                    </p>
                  )}
                </div>
                
                <div className="flex items-center gap-2">
                  {/* Streak badge */}
                  {challenge.streak > 0 && (
                    <div className="flex items-center gap-1 text-xs bg-primary/10 text-primary px-2 py-1 rounded-full">
                      <Flame className="h-3 w-3" />
                      <span>{challenge.streak} day streak</span>
                    </div>
                  )}
                  <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                </div>
              </div>

              {/* Progress indicators */}
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    Day {currentDay} of {totalDays}
                  </span>
                  <span>{challenge.completedDays} days completed ({completionPct}%)</span>
                </div>
                <Progress value={completionPct} className="h-2" />
              </div>

              {/* Today's check-in */}
              <div className="flex items-center gap-2 pt-2 border-t">
                <span className="text-xs text-muted-foreground">Today:</span>
                <button
                  onClick={() => handleCheckin(challenge.id, true)}
                  disabled={savingId === challenge.id}
                  className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs transition-colors ${
                    completedToday
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-primary/10 text-primary hover:bg-primary/20'
                  }`}
                >
                  {completedToday ? (
                    <CheckCircle2 className="h-3 w-3" />
                  ) : (
                    <Circle className="h-3 w-3" />
                  )}
                  {completedToday ? 'Done' : 'Yes'}
                </button>
                <button
                  onClick={() => handleCheckin(challenge.id, false)}
                  disabled={savingId === challenge.id}
                  className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs transition-colors ${
                    hasCheckinToday && !completedToday
                      ? 'bg-muted text-foreground'
                      : 'bg-muted/50 text-muted-foreground hover:bg-muted'
                  }`}
                >
                  {hasCheckinToday && !completedToday ? 'Logged' : 'Not today'}
                </button>
              </div>

              {/* Correlation insights for this challenge */}
              {user?.id && (
                <ChallengeCorrelationsPanel
                  userId={user.id}
                  category={challenge.category}
                  metricSource={challenge.metric_source || challenge.templateMetricSource || 'custom'}
                  metricKey={challenge.metric_key || challenge.templateMetricKey}
                />
              )}
            </div>
          );
        })}
      </CardContent>

      {/* Detail Drawer */}
      <ChallengeDetailDrawer
        challenge={selectedChallenge}
        isOpen={isDetailOpen}
        onClose={handleCloseDetail}
      />
    </Card>
  );
}
